import { sankey, sankeyJustify, sankeyLinkHorizontal } from 'd3-sankey';
import {
  type FlowItem,
  flowCurrencies,
  flowMatches,
  formatAmount,
  linkAmountLabel,
  NO_CURRENCY,
  type SankeyGraph,
  type SankeyLinkData,
  type SankeyNodeData,
} from '../flows';
import { plural } from '../review-inbox';
import { PRINT_COLORS } from '../snapshots';
import { attrId, FIGURE_WIDTH, n1, svgFrame, svgLabel } from './svg';

// The money-flow Sankey as a print figure (full reports), drawn by the same rules as the Money flows screen: payers
// on the left, recipients on the right, one band per payer → recipient pair as wide as the money it carries (a range
// counts as its middle); bands carrying only undisclosed amounts are thin and grey; a loop drops its smallest band
// (listed under the chart); band color = the payer's color, band pattern = review status (solid verified, dashed
// unreviewed, dotted disputed). Pure and deterministic: the same graph always gives the same SVG.

const NODE_W = 12;
const LABEL_ROOM = 170;
const TOP = 12;
// Below the chart: room for the lowest label's amount line, then the legend.
const LEGEND_H = 46;
const LOOP_LINE = 16;
const LOOPS_LISTED = 5;

const DASH: Record<SankeyLinkData['status'], string | null> = { verified: null, unreviewed: '8 5', disputed: '2 4' };

export interface SankeyActor {
  name: string;
  /** Color slot (1–12) from the actor's style; null = no color (grey). */
  slot: number | null;
}

export interface SankeySvgInput {
  graph: SankeyGraph;
  /** Names and colors by actor id; an unknown id is drawn as "Unknown actor". */
  actors: ReadonlyMap<string, SankeyActor>;
  /** The currency key of the figure (ISO code, or `NO_CURRENCY`). */
  currency: string | null;
  title: string;
}

/** Chart height for this many drawn actors: room for a two-line label each. */
export const sankeySvgChartHeight = (nodes: number) => Math.max(220, Math.min(1600, nodes * 34));

export function sankeySvg(input: SankeySvgInput): { svg: string; width: number; height: number } {
  const { graph, actors, currency, title } = input;
  const width = FIGURE_WIDTH;
  const drawn = new Set(graph.links.flatMap(l => [l.source, l.target]));
  const nodesIn = graph.nodes.filter(n => drawn.has(n.id));
  const chartH = graph.links.length ? sankeySvgChartHeight(nodesIn.length) : 60;
  const loops = graph.circular.length
    ? Math.min(LOOPS_LISTED, graph.circular.length) + 1 + (graph.circular.length > LOOPS_LISTED ? 1 : 0)
    : 0;
  const height = Math.round(TOP + chartH + LEGEND_H + loops * LOOP_LINE + 8);
  const actor = (id: string): SankeyActor => actors.get(id) ?? { name: 'Unknown actor', slot: null };

  const parts: string[] = [];
  if (!graph.links.length) {
    parts.push(svgLabel('No money flows to draw.', 20, TOP + 30, { fill: PRINT_COLORS.muted, max: 60 }));
  } else {
    const gen = sankey<SankeyNodeData, SankeyLinkData>()
      .nodeId(d => d.id)
      .nodeWidth(NODE_W)
      .nodePadding(16)
      .nodeAlign(sankeyJustify)
      .nodeSort((a, b) => b.paid + b.received - (a.paid + a.received) || a.id.localeCompare(b.id))
      .extent([
        [LABEL_ROOM, TOP],
        [width - LABEL_ROOM, TOP + chartH],
      ]);
    // d3-sankey mutates what it gets: lay out copies, sorted so the input order never changes the picture.
    const laid = gen({
      nodes: [...nodesIn].sort((a, b) => a.id.localeCompare(b.id)).map(n => ({ ...n })),
      links: [...graph.links].sort((a, b) => a.id.localeCompare(b.id)).map(l => ({ ...l })),
    });
    const path = sankeyLinkHorizontal();
    const bands = laid.links.map(l => {
      const src = l as unknown as SankeyLinkData & { width?: number };
      const payer = typeof l.source === 'object' ? (l.source as SankeyNodeData).id : String(l.source);
      const color = src.nominal ? PRINT_COLORS.line : PRINT_COLORS.entity(actor(payer).slot);
      const dash = DASH[src.status];
      return `<path d="${path(l) ?? ''}" fill="none" stroke="${color}" stroke-opacity="${src.nominal ? 0.6 : 0.45}" stroke-width="${n1(
        Math.max(1.5, src.width ?? 1),
      )}"${dash ? ` stroke-dasharray="${dash}"` : ''} data-link-id="${attrId(src.id)}" data-review-status="${src.status}"${
        src.nominal ? ' data-nominal="true"' : ''
      }/>`;
    });
    parts.push(`<g>${bands.join('')}</g>`);

    const nodes = laid.nodes.map(n => {
      const a = actor(n.id);
      const x0 = n.x0 ?? 0;
      const x1 = n.x1 ?? 0;
      const y0 = n.y0 ?? 0;
      const y1 = n.y1 ?? 0;
      const left = x0 < width / 2;
      const lx = left ? x0 - 6 : x1 + 6;
      const cy = (y0 + y1) / 2;
      const amount = [
        n.received ? `received ${formatAmount(n.received, currency)}` : null,
        n.paid ? `paid ${formatAmount(n.paid, currency)}` : null,
      ]
        .filter(Boolean)
        .join(' · ');
      const align = left ? 'left' : 'right';
      return [
        `<rect x="${n1(x0)}" y="${n1(y0)}" width="${n1(x1 - x0)}" height="${n1(Math.max(2, y1 - y0))}" fill="${PRINT_COLORS.entity(
          a.slot,
        )}" stroke="${PRINT_COLORS.foreground}" stroke-opacity="0.55" stroke-width="1" data-entity-id="${attrId(n.id)}"/>`,
        svgLabel(a.name, lx, cy - 6, { size: 11, weight: 600, max: 26, halo: true, align }),
        svgLabel(amount || 'amount undisclosed', lx, cy + 8, { size: 9, fill: PRINT_COLORS.muted, max: 60, halo: true, align }),
      ].join('');
    });
    parts.push(`<g>${nodes.join('')}</g>`);
  }

  // Legend: what the patterns mean.
  const ly = TOP + chartH + LEGEND_H - 14;
  const sample = (x: number, dash: string | null, color: string, text: string) =>
    `<line x1="${x}" y1="${ly}" x2="${x + 26}" y2="${ly}" stroke="${color}" stroke-width="4"${dash ? ` stroke-dasharray="${dash}"` : ''}/>${svgLabel(
      text,
      x + 32,
      ly,
      { size: 10, fill: PRINT_COLORS.muted, max: 50 },
    )}`;
  parts.push(
    `<g>${[
      sample(20, null, PRINT_COLORS.foreground, 'Verified'),
      sample(130, DASH.unreviewed, PRINT_COLORS.foreground, 'Unreviewed'),
      sample(250, DASH.disputed, PRINT_COLORS.foreground, 'Disputed'),
      sample(360, null, PRINT_COLORS.line, 'Amount undisclosed (width not to scale)'),
    ].join('')}</g>`,
  );

  // Loops: the bands left off the chart, so the figure doesn't hide money.
  if (graph.circular.length) {
    let y = TOP + chartH + LEGEND_H + LOOP_LINE / 2;
    const lines = [svgLabel('Not drawn because they close a loop:', 20, y, { size: 10, weight: 600, fill: PRINT_COLORS.muted, max: 60 })];
    for (const l of graph.circular.slice(0, LOOPS_LISTED)) {
      y += LOOP_LINE;
      lines.push(
        svgLabel(`${actor(l.source).name} → ${actor(l.target).name}: ${linkAmountLabel(l, currency)}`, 28, y, {
          size: 10,
          fill: PRINT_COLORS.muted,
          max: 110,
        }),
      );
    }
    if (graph.circular.length > LOOPS_LISTED) {
      y += LOOP_LINE;
      lines.push(
        svgLabel(`and ${plural(graph.circular.length - LOOPS_LISTED, 'more band')}`, 28, y, {
          size: 10,
          fill: PRINT_COLORS.muted,
          max: 60,
        }),
      );
    }
    parts.push(`<g>${lines.join('')}</g>`);
  }

  return { svg: svgFrame(width, height, title, parts.join('\n')), width, height };
}

// ─── The flows a Sankey figure draws ───────────────────────────────────────────────────────────────────────────

export interface CurrencyGroup {
  /** Currency key: ISO code, or `NO_CURRENCY`. */
  currency: string;
  /** Disclosed total in this currency. */
  total: number;
  flows: FlowItem[];
}

/**
 * The flows touching these entities (all flows when empty), grouped by currency, largest disclosed total first,
 * "no currency" last. A group holds the flows the Money flows screen would draw for that currency: its disclosed
 * amounts, and undisclosed amounts in that currency or with none. When no amount is disclosed at all, the
 * undisclosed flows make one group per currency they name (or one "no currency" group).
 */
export function flowsByCurrency(flows: readonly FlowItem[], entityIds: readonly string[]): CurrencyGroup[] {
  const ids = new Set(entityIds);
  const touching = ids.size ? flows.filter(f => ids.has(f.from) || ids.has(f.to)) : [...flows];
  if (!touching.length) return [];
  const totals = new Map(flowCurrencies(touching).map(c => [c.key, c.total]));
  for (const f of touching) if (f.currency && !totals.has(f.currency)) totals.set(f.currency, 0);
  if (!totals.size) totals.set(NO_CURRENCY, 0);
  const last = (k: string) => (k === NO_CURRENCY ? 1 : 0);
  return [...totals.entries()]
    .sort(([a, ta], [b, tb]) => last(a) - last(b) || tb - ta || a.localeCompare(b))
    .map(([currency, total]) => ({ currency, total, flows: touching.filter(f => flowMatches(f, { currency })) }))
    .filter(g => g.flows.length);
}

const among = (entityIds: readonly string[]) => (entityIds.length ? "among the report's results" : 'among all actors');

export function sankeyCaption(currency: string, flows: number, entityIds: readonly string[]): string {
  const what = currency === NO_CURRENCY ? 'Money flows with no currency stated' : `Money flows in ${currency}`;
  return `${what} ${among(entityIds)} (${plural(flows, 'flow')})`;
}
